import { useState, useEffect } from 'react';
import { 
  Gift, 
  Plus,
  Edit,
  Tag,
  Percent,
  Calendar,
  Users,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { bonusesAPI, promoCodesAPI } from '../lib/api';
import { formatCurrency, formatDateTime, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  const color = status === true || status === 'active' ? 'success' : 'danger';
  const colorClasses = {
    success: 'badge-success',
    danger: 'badge-danger',
  };
  
  return (
    <span className={`badge ${colorClasses[color]}`}>
      {status === true || status === 'active' ? 'Active' : 'Inactive'}
    </span>
  );
};

export default function Bonuses() {
  const [bonuses, setBonuses] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bonuses');
  const [bonusDialog, setBonusDialog] = useState(false);
  const [promoDialog, setPromoDialog] = useState(false);
  const [newBonus, setNewBonus] = useState({
    name: '',
    type: 'welcome',
    amount: 0,
    percentage: 100,
    min_deposit: 100,
    max_bonus: 5000,
    wagering_multiplier: 1,
    validity_days: 30,
    is_active: true,
  });
  const [newPromo, setNewPromo] = useState({
    code: '',
    bonus_id: '',
    bonus_name: '',
    usage_limit: 100,
    is_single_use: false,
    expiry_date: '',
    is_active: true,
  });

  const fetchBonuses = async () => {
    try {
      setLoading(true);
      const response = await bonusesAPI.getAll();
      setBonuses(response.data);
    } catch (error) {
      toast.error('Failed to fetch bonuses');
    } finally {
      setLoading(false);
    }
  };

  const fetchPromoCodes = async () => {
    try {
      setLoading(true);
      const response = await promoCodesAPI.getAll();
      setPromoCodes(response.data);
    } catch (error) {
      toast.error('Failed to fetch promo codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBonuses();
    fetchPromoCodes();
  }, []);

  const handleCreateBonus = async () => {
    try {
      await bonusesAPI.create(newBonus);
      toast.success('Bonus created');
      setBonusDialog(false);
      setNewBonus({
        name: '',
        type: 'welcome',
        amount: 0,
        percentage: 100,
        min_deposit: 100,
        max_bonus: 5000,
        wagering_multiplier: 1,
        validity_days: 30,
        is_active: true,
      });
      fetchBonuses();
    } catch (error) {
      toast.error('Failed to create bonus');
    }
  };

  const handleCreatePromo = async () => {
    try {
      const selectedBonus = bonuses.find(b => b.id === newPromo.bonus_id);
      await promoCodesAPI.create({
        ...newPromo,
        bonus_name: selectedBonus?.name || '',
        expiry_date: new Date(newPromo.expiry_date).toISOString(),
      });
      toast.success('Promo code created');
      setPromoDialog(false);
      setNewPromo({
        code: '',
        bonus_id: '',
        bonus_name: '',
        usage_limit: 100,
        is_single_use: false,
        expiry_date: '',
        is_active: true,
      });
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to create promo code');
    }
  };

  const handleToggleBonus = async (id, isActive) => {
    try {
      await bonusesAPI.update(id, { is_active: !isActive });
      toast.success('Bonus updated');
      fetchBonuses();
    } catch (error) {
      toast.error('Failed to update bonus');
    }
  };

  const bonusTypeLabels = {
    welcome: 'Welcome Bonus',
    deposit: 'Deposit Bonus',
    cashback: 'Cashback',
    referral: 'Referral Bonus',
    vip: 'VIP Bonus',
    festival: 'Festival Bonus',
    loss_recovery: 'Loss Recovery',
  };

  return (
    <div data-testid="bonuses-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Bonuses & Promotions</h1>
        <p className="page-subtitle">Manage bonus offers and promo codes</p>
      </div>

      {/* Stats */}
      <div className="widget-grid mb-6">
        <div className="stat-card">
          <Gift className="w-5 h-5 text-primary mb-2" />
          <p className="stat-value">{bonuses.filter(b => b.is_active).length}</p>
          <p className="stat-label">Active Bonuses</p>
        </div>
        <div className="stat-card">
          <Tag className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="stat-value">{promoCodes.filter(p => p.is_active).length}</p>
          <p className="stat-label">Active Promo Codes</p>
        </div>
        <div className="stat-card">
          <Users className="w-5 h-5 text-blue-400 mb-2" />
          <p className="stat-value">{bonuses.reduce((sum, b) => sum + (b.usage_count || 0), 0)}</p>
          <p className="stat-label">Total Bonus Claims</p>
        </div>
        <div className="stat-card">
          <Percent className="w-5 h-5 text-amber-400 mb-2" />
          <p className="stat-value">{formatCurrency(125000)}</p>
          <p className="stat-label">Bonus Liability</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="bonuses" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Gift className="w-4 h-4 mr-2" />
            Bonuses
          </TabsTrigger>
          <TabsTrigger value="promos" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Tag className="w-4 h-4 mr-2" />
            Promo Codes
          </TabsTrigger>
        </TabsList>

        {/* Bonuses Tab */}
        <TabsContent value="bonuses">
          <div className="data-table-container">
            <div className="data-table-header">
              <h3 className="data-table-title">All Bonuses</h3>
              <Button onClick={() => setBonusDialog(true)} data-testid="create-bonus-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Bonus
              </Button>
            </div>

            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Bonus Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead className="text-right">Max Bonus</TableHead>
                  <TableHead>Wagering</TableHead>
                  <TableHead>Validity</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                    </TableCell>
                  </TableRow>
                ) : bonuses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                      No bonuses found
                    </TableCell>
                  </TableRow>
                ) : (
                  bonuses.map((bonus) => (
                    <TableRow key={bonus.id} className="table-row" data-testid={`bonus-row-${bonus.id}`}>
                      <TableCell className="font-medium">{bonus.name}</TableCell>
                      <TableCell>
                        <span className="badge badge-info">{bonusTypeLabels[bonus.type] || bonus.type}</span>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {bonus.percentage > 0 ? `${bonus.percentage}%` : formatCurrency(bonus.amount)}
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(bonus.max_bonus)}</TableCell>
                      <TableCell className="font-mono">{bonus.wagering_multiplier}x</TableCell>
                      <TableCell>{bonus.validity_days} days</TableCell>
                      <TableCell className="font-mono">{bonus.usage_count || 0}</TableCell>
                      <TableCell>
                        <StatusBadge status={bonus.is_active} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Switch
                          checked={bonus.is_active}
                          onCheckedChange={() => handleToggleBonus(bonus.id, bonus.is_active)}
                          data-testid={`toggle-bonus-${bonus.id}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Promo Codes Tab */}
        <TabsContent value="promos">
          <div className="data-table-container">
            <div className="data-table-header">
              <h3 className="data-table-title">All Promo Codes</h3>
              <Button onClick={() => setPromoDialog(true)} data-testid="create-promo-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Promo Code
              </Button>
            </div>

            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Code</TableHead>
                  <TableHead>Bonus</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Expiry</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                    </TableCell>
                  </TableRow>
                ) : promoCodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                      No promo codes found
                    </TableCell>
                  </TableRow>
                ) : (
                  promoCodes.map((promo) => (
                    <TableRow key={promo.id} className="table-row">
                      <TableCell className="font-mono font-semibold text-primary">{promo.code}</TableCell>
                      <TableCell>{promo.bonus_name}</TableCell>
                      <TableCell className="font-mono">{promo.used_count}/{promo.usage_limit}</TableCell>
                      <TableCell>
                        <span className={`badge ${promo.is_single_use ? 'badge-warning' : 'badge-info'}`}>
                          {promo.is_single_use ? 'Single Use' : 'Multi Use'}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">{formatDateTime(promo.expiry_date)}</TableCell>
                      <TableCell>
                        <StatusBadge status={promo.is_active} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Bonus Dialog */}
      <Dialog open={bonusDialog} onOpenChange={setBonusDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Bonus</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Bonus Name</Label>
              <Input
                placeholder="Enter bonus name"
                value={newBonus.name}
                onChange={(e) => setNewBonus({ ...newBonus, name: e.target.value })}
                data-testid="bonus-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={newBonus.type}
                onValueChange={(v) => setNewBonus({ ...newBonus, type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="welcome">Welcome Bonus</SelectItem>
                  <SelectItem value="deposit">Deposit Bonus</SelectItem>
                  <SelectItem value="cashback">Cashback</SelectItem>
                  <SelectItem value="referral">Referral Bonus</SelectItem>
                  <SelectItem value="vip">VIP Bonus</SelectItem>
                  <SelectItem value="festival">Festival Bonus</SelectItem>
                  <SelectItem value="loss_recovery">Loss Recovery</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Percentage</Label>
                <Input
                  type="number"
                  value={newBonus.percentage}
                  onChange={(e) => setNewBonus({ ...newBonus, percentage: parseFloat(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Fixed Amount</Label>
                <Input
                  type="number"
                  value={newBonus.amount}
                  onChange={(e) => setNewBonus({ ...newBonus, amount: parseFloat(e.target.value) })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Deposit</Label>
                <Input
                  type="number"
                  value={newBonus.min_deposit}
                  onChange={(e) => setNewBonus({ ...newBonus, min_deposit: parseFloat(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Bonus</Label>
                <Input
                  type="number"
                  value={newBonus.max_bonus}
                  onChange={(e) => setNewBonus({ ...newBonus, max_bonus: parseFloat(e.target.value) })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Wagering Multiplier</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newBonus.wagering_multiplier}
                  onChange={(e) => setNewBonus({ ...newBonus, wagering_multiplier: parseFloat(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Validity (Days)</Label>
                <Input
                  type="number"
                  value={newBonus.validity_days}
                  onChange={(e) => setNewBonus({ ...newBonus, validity_days: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={newBonus.is_active}
                onCheckedChange={(v) => setNewBonus({ ...newBonus, is_active: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBonusDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateBonus}
              disabled={!newBonus.name}
              data-testid="confirm-create-bonus-btn"
            >
              Create Bonus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Promo Dialog */}
      <Dialog open={promoDialog} onOpenChange={setPromoDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Promo Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Promo Code</Label>
              <Input
                placeholder="e.g., WELCOME50"
                value={newPromo.code}
                onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                data-testid="promo-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Linked Bonus</Label>
              <Select
                value={newPromo.bonus_id}
                onValueChange={(v) => setNewPromo({ ...newPromo, bonus_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select bonus" />
                </SelectTrigger>
                <SelectContent>
                  {bonuses.map((bonus) => (
                    <SelectItem key={bonus.id} value={bonus.id}>{bonus.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Usage Limit</Label>
              <Input
                type="number"
                value={newPromo.usage_limit}
                onChange={(e) => setNewPromo({ ...newPromo, usage_limit: parseInt(e.target.value) })}
              />
            </div>
            <div className="space-y-2">
              <Label>Expiry Date</Label>
              <Input
                type="datetime-local"
                value={newPromo.expiry_date}
                onChange={(e) => setNewPromo({ ...newPromo, expiry_date: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Single Use Per User</Label>
              <Switch
                checked={newPromo.is_single_use}
                onCheckedChange={(v) => setNewPromo({ ...newPromo, is_single_use: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreatePromo}
              disabled={!newPromo.code || !newPromo.bonus_id || !newPromo.expiry_date}
              data-testid="confirm-create-promo-btn"
            >
              Create Promo Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
